import { Injectable } from '@angular/core';
import { TelemetryValueDto } from 'chronos-panda-client';
import { DisplayWindow } from './display-window';

interface ResultSet {
  speedSeries: TelemetryValueDto[];
  errorSeries: TelemetryValueDto[];
}

@Injectable({
  providedIn: 'root'
})
export class FilterAlgorithmService {
  public static readingAnomalyFilter(signal: TelemetryValueDto[]): TelemetryValueDto[] {
    if (signal.length < 3) {
      return signal; // Not enough data to filter
    }

    const filtered: TelemetryValueDto[] = [];

    for (let i = 0; i < signal.length; i++) {
      if (i === 0 || i === signal.length - 1) {
        filtered.push(signal[i]); // Keep first and last points
        continue;
      }

      const prevValue = signal[i - 1].value;
      const currentValue = signal[i].value;
      const nextValue = signal[i + 1].value;

      // Check if current value is an anomaly
      const threshold = Math.abs(prevValue) * 0.2;
      if (
        threshold > 0 &&
        ((prevValue < currentValue && prevValue > nextValue) || (prevValue > currentValue && prevValue < nextValue)) &&
        Math.abs(prevValue - (currentValue + nextValue) / 2) < threshold
      ) {
        filtered.push({
          timestamp: signal[i].timestamp,
          value: (currentValue + nextValue) / 2
        });
        filtered.push({
          timestamp: signal[i + 1].timestamp,
          value: (currentValue + nextValue) / 2
        });
        i++; // Skip the next point as it's likely an anomaly
        continue;
      }

      filtered.push(signal[i]);
    }

    return filtered;
  }

  public static movingAverageFilter(signal: TelemetryValueDto[], windowSize: number): TelemetryValueDto[] {
    if (windowSize < 1) {
      throw new Error('Window size must be at least 1');
    }

    if (windowSize < 3) {
      return signal; // No smoothing needed for window size < 3
    }

    const smoothed: TelemetryValueDto[] = [];
    const halfWindow = Math.max(1, Math.floor(windowSize / 2));

    for (let i = 0; i < signal.length; i++) {
      let sum = 0;
      let count = 0;

      for (let j = -halfWindow; j <= halfWindow; j++) {
        const index = i + j;
        if (index >= 0 && index < signal.length) {
          sum += signal[index].value;
          count++;
        }
      }

      smoothed.push({
        timestamp: signal[i].timestamp,
        value: sum / count
      });
    }

    return smoothed;
  }

  /**
   * Applies a median filter to a time series signal.
   * The median filter is effective at removing outliers and noise while preserving edges.
   * @param signal Array of telemetry values representing the time series
   * @param windowSize Size of the sliding window (should be odd for symmetrical filtering)
   * @returns Filtered signal with median values
   */
  public static medianFilter(signal: TelemetryValueDto[], windowSize: number): TelemetryValueDto[] {
    if (windowSize < 1) {
      throw new Error('Window size must be at least 1');
    }

    if (windowSize < 3) {
      return signal; // No filtering needed for window size < 3
    }

    // Ensure window size is odd for symmetrical filtering
    if (windowSize % 2 === 0) {
      windowSize += 1;
    }

    const filtered: TelemetryValueDto[] = [];
    const halfWindow = Math.floor(windowSize / 2);

    for (let i = 0; i < signal.length; i++) {
      const windowValues: number[] = [];

      // Collect values within the window
      for (let j = -halfWindow; j <= halfWindow; j++) {
        const index = i + j;
        if (index >= 0 && index < signal.length) {
          windowValues.push(signal[index].value);
        }
      }

      // Calculate median
      const median = FilterAlgorithmService.calculateMedian(windowValues);

      filtered.push({
        timestamp: signal[i].timestamp,
        value: median
      });
    }

    return filtered;
  }

  /**
   * Calculates the median value from an array of numbers.
   * @param values Array of numbers
   * @returns Median value
   */
  private static calculateMedian(values: number[]): number {
    if (values.length === 0) {
      return 0;
    }

    // Sort the values
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);

    // If odd number of elements, return the middle one
    // If even number of elements, return the average of the two middle ones
    if (sorted.length % 2 === 1) {
      return sorted[mid];
    } else {
      return (sorted[mid - 1] + sorted[mid]) / 2;
    }
  }

  /**
   * Applies a LOESS (Locally Estimated Scatterplot Smoothing) filter to a time series signal.
   * This implementation first applies a median filter to remove outliers, then uses LOESS smoothing
   * on the median-filtered data. LOESS is a non-parametric regression method that combines local
   * polynomial regression with a weighting function.
   * This combination is effective at removing outliers while smoothing noisy data and preserving local trends.
   * The filter is only applied to segments where values > 0 for more than 40 seconds.
   * @param signal Array of telemetry values representing the time series
   * @param windowSize Size of the sliding window (fraction of data points to use, should be odd for symmetrical filtering)
   * @param polyOrder Polynomial order for local regression (1 for linear, 2 for quadratic)
   * @returns Filtered signal with median-LOESS smoothed values for qualifying segments
   */
  public static loessFilter(signal: TelemetryValueDto[], windowSize: number, polyOrder: number = 1): TelemetryValueDto[] {
    if (windowSize < 1) {
      throw new Error('Window size must be at least 1');
    }

    if (windowSize < 3) {
      return signal; // No filtering needed for window size < 3
    }

    if (polyOrder < 0 || polyOrder > 2) {
      throw new Error('Polynomial order must be 0, 1, or 2');
    }

    // Ensure window size is odd for symmetrical filtering
    if (windowSize % 2 === 0) {
      windowSize += 1;
    }

    // Step 1: Identify segments where values > 0 for more than 40 seconds
    const segmentMinimumDuration = 40 * 1000; // 40 seconds in milliseconds
    const segments: { startIndex: number; endIndex : number; shouldFilter: boolean }[] = [];

    let segmentStartIndex = 0;
    let segmentStartTime = new Date(signal[0].timestamp).getTime();
    let inPositiveSegment = signal[0]?.value > 0;

    for (let i = 1; i < signal.length; i++) {
      const currentPositive = signal[i].value > 0;

      if (currentPositive !== inPositiveSegment) {
        // Segment boundary detected
        if (currentPositive) {
            segmentStartTime = new Date(signal[i].timestamp).getTime();
        }

        const segmentDuration = new Date(signal[i - 1].timestamp).getTime() - segmentStartTime;
        const shouldFilter = inPositiveSegment && segmentDuration > segmentMinimumDuration;

        segments.push({
          startIndex: segmentStartIndex,
          endIndex: i - 1,
          shouldFilter
        });

        segmentStartIndex = i;
        inPositiveSegment = currentPositive;
      }
    }

    // Add the last segment
    if (segmentStartIndex < signal.length) {
      const segmentDuration = new Date(signal[signal.length - 1].timestamp).getTime() - segmentStartTime;
      const shouldFilter = inPositiveSegment && segmentDuration > segmentMinimumDuration;

      segments.push({
        startIndex: segmentStartIndex,
        endIndex: signal.length - 1,
        shouldFilter
      });
    }

    // Step 2: Apply median filter first to remove outliers (only for segments that will be filtered)
    const medianFiltered = FilterAlgorithmService.medianFilter(signal, windowSize);

    // Step 3: Apply LOESS smoothing to qualifying segments
    const filtered: TelemetryValueDto[] = [];
    const halfWindow = Math.floor(windowSize / 2);

    for (let i = 0; i < signal.length; i++) {
      // Check if this index is in a segment that should be filtered
      const segment = segments.find(seg => i >= seg.startIndex && i <= seg.endIndex);

      if (!segment || !segment.shouldFilter) {
        // Return original value for non-qualifying segments
        filtered.push({
          timestamp: signal[i].timestamp,
          value: signal[i].value
        });
        continue;
      }

      // Collect values and positions within the window from median-filtered data
      const windowData: { relativePos: number; value: number; distance: number; weight: number }[] = [];

      for (let j = -halfWindow; j <= halfWindow; j++) {
        const index = i + j;
        if (index >= 0 && index < medianFiltered.length) {
          const distance = Math.abs(j);
          windowData.push({
            relativePos: j,
            value: medianFiltered[index].value,
            distance,
            weight: 0 // Will be calculated
          });
        }
      }

      // Calculate tricube weights based on distance
      const maxDistance = Math.max(...windowData.map(d => d.distance));
      if (maxDistance > 0) {
        windowData.forEach(d => {
          const normalizedDistance = d.distance / maxDistance;
          // Tricube weight function: (1 - |d|^3)^3
          d.weight = Math.pow(1 - Math.pow(normalizedDistance, 3), 3);
        });
      } else {
        // All points are at the same position
        windowData.forEach(d => {
          d.weight = 1;
        });
      }

      // Fit weighted polynomial regression
      const fittedValue = FilterAlgorithmService.fitWeightedPolynomial(windowData, 0, polyOrder);

      filtered.push({
        timestamp: signal[i].timestamp,
        value: fittedValue
      });
    }

    return filtered;
  }

  /**
   * Fits a weighted polynomial regression to the given data points.
   * @param data Array of data points with positions, values, and weights
   * @param x Position at which to evaluate the polynomial
   * @param polyOrder Polynomial order (0 = constant, 1 = linear, 2 = quadratic)
   * @returns Fitted value at position x
   */
  private static fitWeightedPolynomial(
    data: { relativePos: number; value: number; distance: number; weight: number }[],
    x: number,
    polyOrder: number
  ): number {
    if (data.length === 0) {
      return 0;
    }

    // For polyOrder = 0, return weighted average
    if (polyOrder === 0) {
      let weightedSum = 0;
      let weightSum = 0;
      for (const point of data) {
        weightedSum += point.weight * point.value;
        weightSum += point.weight;
      }
      return weightSum > 0 ? weightedSum / weightSum : 0;
    }

    // For polyOrder >= 1, solve weighted least squares using normal equations
    // We need to solve: (X^T * W * X) * beta = X^T * W * y
    // where X is the design matrix, W is the weight matrix, y is the values

    const n = polyOrder + 1;
    const matrix: number[][] = Array(n).fill(0).map(() => Array(n).fill(0));
    const vector: number[] = Array(n).fill(0);

    // Build the normal equations
    for (const point of data) {
      const relativePos = point.relativePos;
      const w = point.weight;
      const y = point.value;

      for (let i = 0; i < n; i++) {
        const xi = Math.pow(relativePos, i);
        vector[i] += w * xi * y;

        for (let j = 0; j < n; j++) {
          const xj = Math.pow(relativePos, j);
          matrix[i][j] += w * xi * xj;
        }
      }
    }

    // Solve the system using Gaussian elimination
    const coefficients = FilterAlgorithmService.gaussianElimination(matrix, vector);

    if (!coefficients) {
      // Fallback to weighted average if solution fails
      let weightedSum = 0;
      let weightSum = 0;
      for (const point of data) {
        weightedSum += point.weight * point.value;
        weightSum += point.weight;
      }
      return weightSum > 0 ? weightedSum / weightSum : 0;
    }

    // Evaluate polynomial at position x (which is 0 for the center point)
    let result = 0;
    for (let i = 0; i < coefficients.length; i++) {
      result += coefficients[i] * Math.pow(x, i);
    }

    return result;
  }

  /**
   * Solves a system of linear equations using Gaussian elimination with partial pivoting.
   * @param A Coefficient matrix
   * @param b Right-hand side vector
   * @returns Solution vector, or null if the system is singular
   */
  private static gaussianElimination(A: number[][], b: number[]): number[] | null {
    const n = b.length;
    const augmented: number[][] = A.map((row, i) => [...row, b[i]]);

    // Forward elimination with partial pivoting
    for (let col = 0; col < n; col++) {
      // Find pivot
      let maxRow = col;
      for (let row = col + 1; row < n; row++) {
        if (Math.abs(augmented[row][col]) > Math.abs(augmented[maxRow][col])) {
          maxRow = row;
        }
      }

      // Swap rows
      [augmented[col], augmented[maxRow]] = [augmented[maxRow], augmented[col]];

      // Check for singular matrix
      if (Math.abs(augmented[col][col]) < 1e-10) {
        return null;
      }

      // Eliminate column
      for (let row = col + 1; row < n; row++) {
        const factor = augmented[row][col] / augmented[col][col];
        for (let j = col; j <= n; j++) {
          augmented[row][j] -= factor * augmented[col][j];
        }
      }
    }

    // Back substitution
    const solution: number[] = Array(n).fill(0);
    for (let row = n - 1; row >= 0; row--) {
      let sum = augmented[row][n];
      for (let col = row + 1; col < n; col++) {
        sum -= augmented[row][col] * solution[col];
      }
      solution[row] = sum / augmented[row][row];
    }

    return solution;
  }

  /**
   * Applies the Savitzky–Golay filter to a 1D signal.
   * @param signal Array of numbers representing the signal
   * @param windowSize Must be odd number greater than 2
   * @param polyOrder Polynomial order (usually 2 or 3)
   * @returns Smoothed signal
   */
  public static savitzkyGolay(signal: TelemetryValueDto[], windowSize: number, polyOrder: number = 2): TelemetryValueDto[] {
    if (windowSize % 2 !== 1 || windowSize < 3) {
      throw new Error('windowSize must be an odd number >= 3');
    }

    if (polyOrder >= windowSize) {
      throw new Error('polyOrder must be less than windowSize');
    }

    const half = Math.max(1, Math.floor(windowSize / 2));
    const coefficients = FilterAlgorithmService.getSavitzkyGolayCoefficients(windowSize, polyOrder);
    const result: TelemetryValueDto[] = [];

    for (let i = 0; i < signal.length; i++) {
      let sum = 0;
      for (let j = -half; j <= half; j++) {
        const idx = i + j;
        const clampedIndex = Math.max(0, Math.min(signal.length - 1, idx));
        sum += coefficients[j + half] * signal[clampedIndex].value;
      }
      result.push({
        timestamp: signal[i].timestamp,
        value: Math.max(0, sum)
      });
    }

    return result;
  }

  /**
   * Generates Savitzky–Golay filter coefficients.
   * Currently supports polyOrder = 2 only.
   * @param windowSize Must be odd number
   * @param polyOrder Polynomial order (only 2 supported in this demo)
   * @returns Array of convolution coefficients
   */
  private static getSavitzkyGolayCoefficients(windowSize: number, polyOrder: number): number[] {
    if (polyOrder !== 2) {
      throw new Error('Only polynomial order 2 is implemented in this demo');
    }

    // Coefficients for polyOrder = 2 and various window sizes (odd numbers)
    const coeffsMap: { [key: number]: number[] } = {
      3: [-1, 2, -1].map((c) => c / 2),
      5: [-3, 12, 17, 12, -3].map((c) => c / 35),
      7: [-2, 3, 6, 7, 6, 3, -2].map((c) => c / 21),
      9: [-21, 14, 39, 54, 59, 54, 39, 14, -21].map((c) => c / 231),
      11: [-36, 9, 44, 69, 84, 89, 84, 69, 44, 9, -36].map((c) => c / 429),
      13: [-11, 0, 9, 16, 21, 24, 25, 24, 21, 16, 9, 0, -11].map((c) => c / 143),
      15: [-78, -13, 42, 87, 122, 147, 162, 167, 162, 147, 122, 87, 42, -13, -78].map((c) => c / 1105),
      17: [-21, -6, 7, 18, 27, 34, 39, 42, 43, 42, 39, 34, 27, 18, 7, -6, -21].map((c) => c / 323),
      19: [-136, -51, 24, 89, 144, 189, 224, 249, 264, 269, 264, 249, 224, 189, 144, 89, 24, -51, -136]
        .map((c) => c / 2261),
      21: [-171, -76, 9, 84, 149, 204, 249, 284, 309, 324, 329, 324, 309, 284, 249, 204, 149, 84, 9, -76, -171]
        .map((c) => c / 3059),
      23: [-253, -138, -33, 62, 147, 222, 287, 342, 387, 422, 447, 462, 447, 422, 387, 342, 287, 222, 147, 62, -33, -138, -253]
        .map((c) => c / 4845),
      25: [-210, -120, -45, 15, 60, 90, 105, 105, 90, 60, 15, -45, -120, -210, -315, -210, -120, -45, 15, 60, 90, 105, 105, 90, 60]
        .map((c) => c / 5175),
      27: [-325, -195, -85, 5, 75, 125, 155, 165, 155, 125, 75, 5, -85, -195, -325, -195, -85, 5, 75, 125, 155, 165, 155, 125, 75, 5, -85]
        .map((c) => c / 7803),
      29: [-78, -49, -24, -3, 14, 27, 36, 41, 42, 39, 32, 21, 6, -13, -36, -13, 6, 21, 32, 39, 42, 41, 36, 27, 14, -3, -24, -49, -78]
        .map((c) => c / 2145),
      31: [
        -465, -300, -165, -60, 15, 60, 75, 60, 15, -60, -165, -300, -465, -300, -165, -60,
        15, 60, 75, 60, 15, -60, -165, -300, -465, -300, -165, -60, 15, 60, 75
      ].map((c) => c / 11628)
    };

    const coeffs = coeffsMap[windowSize];
    if (!coeffs) {
      throw new Error(`Unsupported window size ${windowSize} for polyOrder 2, supported sizes are up to window size 31`);
    }

    return coeffs;
  }

  public static speedGapFilter(speedSignal: TelemetryValueDto[], displayWindow: DisplayWindow): ResultSet {
    // when the timestamp of two concecutive readings are farther apart than 1 minute, create new
    // readings every 10 seconds with a null value. Create a new list of readings for this time range.
    const filtered: TelemetryValueDto[] = [];
    const oneMinute = 60 * 1000; // 1 minute in milliseconds
    const speedQueryFactor = displayWindow.calculateSecondsPerTenPixels();
    const gapThreshold = Math.max(oneMinute, speedQueryFactor * 1000);

    let lastTimestamp = new Date(displayWindow.start).getTime();

    for (let i = 0; i < speedSignal.length; i++) {
      const currentTimestamp = new Date(speedSignal[i].timestamp).getTime();
      const timeDiff = currentTimestamp - lastTimestamp;

      if (timeDiff > gapThreshold) {
        this.fillGap(filtered, lastTimestamp, currentTimestamp, speedQueryFactor);
      }

      // Add the current reading
      filtered.push(speedSignal[i]);
      lastTimestamp = currentTimestamp;
    }

    if (lastTimestamp + gapThreshold < new Date(displayWindow.end).getTime()) {
      this.fillGap(filtered, lastTimestamp, new Date(displayWindow.end).getTime(), speedQueryFactor);
    }

    // Create error series based on the filtered speed series; set it to 1 when the speed has a null value
    const errorSeries: TelemetryValueDto[] = [];
    for (let i = 0; i < filtered.length; i++) {
      errorSeries.push({
        timestamp: filtered[i].timestamp,
        value: filtered[i].value === null ? 1 : null
      });
    }

    return {
      speedSeries: filtered,
      errorSeries
    };
  }

  private static fillGap(filtered: TelemetryValueDto[], lastTimestamp: number, currentTimestamp: number, speedQueryFactor: number) {
    const tenSeconds = 10 * 1000; // 10 seconds in milliseconds
    const gapThreshold = Math.max(tenSeconds, speedQueryFactor * 1000);
    if (lastTimestamp >= currentTimestamp) {
      return; // No gap to fill
    }
    // Create new readings every 10 seconds until we reach the current timestamp
    for (let j = lastTimestamp; j < currentTimestamp; j += gapThreshold) {
      filtered.push({
        timestamp: new Date(j).toISOString(),
        value: null // Null value for these interpolated readings
      });
    }
    // Ensure we add a null reading exactly at the current timestamp if not already added
    if (lastTimestamp < currentTimestamp) {
      filtered.push({
        timestamp: new Date(currentTimestamp).toISOString(),
        value: null // Null value for these interpolated readings
      });
    }
  }
}
